import React from "react";
import { Button, Icon, IconId, Text } from "src/components";
import { Badge } from "src/components/badge";
import { theme } from "src/config";
import { Item } from "src/models";
import styled from "styled-components";
import { Column, Row, Spacer } from "./gridElements";
import { Tooltip } from "src/components";
import { useTranslation } from "react-i18next";
import { IItem } from "src/types";

export interface SubView {
  iconId:    IconId,
  key:       string,
  title:     string,
  body:      React.ReactNode,
  hidden?:   boolean,
  count?:    number,
  disabled?: boolean,
  tooltipText?:string,
}

export interface ISubViewBarProps {
  subViews: SubView[],
  item:     Item<IItem>,
}

export const SubViewBar = React.forwardRef((props: ISubViewBarProps, ref): JSX.Element => {
  const { subViews, item } = props;
  const [height,       setHeight]       = React.useState<string>(undefined);
  const [clientHeight, setClientHeight] = React.useState<string>(undefined);
  const [curSubView,   setCurSubView]   = React.useState<SubView>(undefined);

  React.useEffect(() => {
    const updateHeight = () => {
      setClientHeight(document.getElementById("content_body")?.clientHeight + "px")
    }
    updateHeight();

    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  React.useEffect(() => {
    if(height) setHeight(clientHeight);
  }, [clientHeight])

  const openView = (view: SubView): void => {
    if(view) {
      setHeight(clientHeight);
      setCurSubView(view);
    } else {
      setHeight(undefined);
      setCurSubView(null);
    }
  }
  
  React.useImperativeHandle(ref, () => ({
    setActive: (key: string): void => openView(subViews.find(sv => sv.key === key))
  }));
  
  return (
    <Container>
      <Row>
        <Spacer width="2rem" />
        {subViews.map(sv => sv.hidden ? null : (
          <React.Fragment key={sv.key}>
            <WedgeContainer>
              {/* <Tooltip content={sv.title}> */}
              <Button variant="icon" onClick={() => openView(sv)} disabled={sv.disabled || !item.canEdit}>
              <Icon iconId={sv.iconId} size="xlarge" cursor="pointer" color={theme.font.color.black} />
              </Button>
              {/* </Tooltip> */}
              { !!sv.count && (<Badge borderColor={theme.color.black} width="1.8rem" height="1.8rem" color={theme.color.black} top="-4.25rem" right="-1.7rem"  content={sv.count}/>) }
              <Wedge />
            </WedgeContainer>
            <Spacer width="4rem" />
          </React.Fragment>
        ))}
      </Row>
      <ViewContainer height={height ? `calc(${height} - 2rem)`: "0"}>
        <ButtonContainer hidden={!Boolean(height)}>
          <Button variant="icon" onClick={() => openView(null)}>
            <Icon iconId={"chevronDown"} size="medium" cursor="pointer" color={theme.font.color.black} />
          </Button>
        </ButtonContainer>
        <Column>
          <TitleContainer>
            <Icon iconId={curSubView?.iconId} size="large" cursor="pointer" color={theme.font.color.black} />
            <Text size="default" as="span">{curSubView?.title}</Text>
          </TitleContainer>
          {curSubView?.body}
        </Column>
      </ViewContainer>
    </Container>
  );
});


const Container = styled.div`
  border-bottom:    2px solid ${theme.color.secondaryBorder};
  position:         relative;
  background-color: transparent;
`;

const ButtonContainer = styled.div`
  margin-top: 0.2rem;
  position:   absolute;
  top:        0;
  left:       50%;
  z-index:    501;
`;

const TitleContainer = styled.div`
  display:       flex;
  align-items:   center;
  margin-bottom: 1.8rem;
  flex-direction: row;
  gap: 0.75rem;
  svg {
    width: 2.4rem;
  }
`;

const WedgeContainer = styled.div`
  display:        flex;
  flex-direction: column;
  align-items:    center;
`;

const Wedge = styled.div`
  width:         0;
  height:        0;
  border-left:   7px solid transparent;
  border-right:  7px solid transparent;
  border-bottom: 7px solid ${theme.color.secondaryBorder};
`;

const ViewContainer = styled.div<{height?: string}>`
  height:           ${props => props.height || 0};
  overflow:         hidden;
  display:          flex;
  width:            100%;
  background-color: white;
  z-index:          500;
  position:         absolute;
  padding:          ${props => props.height !== "0" ? "0.6rem" : 0};
  bottom:           0;
  transition:       height 0.2s ease-in-out;
  border-top:       ${props => props.height !== "0" ? `2px solid ${theme.color.secondaryBorder}` : undefined};
  border-left:      3px solid white;
  border-right:     3px solid white;
`;